/**
 * RubricScoreGrid - Reusable rubric scoring interface
 * Strictly matching ProgrammingActivityV2 RubricViewV2 structure and styling.
 */
import React, { useState, useEffect } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  FiMessageSquare,
  FiList,
  FiGrid,
  FiCheck,
  FiArrowRight,
  FiRotateCcw,
  FiX,
} from 'react-icons/fi';
import styles from './Rubrics.module.css';

const RubricScoreGrid = ({
  rubric = [],
  initialEvaluation = [],
  initialRemark = '',
  maxQuestionMarks = 0,
  onSaveGrade,
  onClose,
  onNextStudent,
  hasNextStudent = false,
  isSaving = false,
  isStudentView = false,
  readOnly = false,
  isEditQuestion = false,
}) => {
  // Selections: { [criteriaId]: { ratingId, marks, comments } }
  const [selections, setSelections] = useState({});
  const [overallRemark, setOverallRemark] = useState(initialRemark || '');
  const [showCommentBox, setShowCommentBox] = useState({});
  const [viewMode, setViewMode] = useState('grid');

  // Sync initial state
  useEffect(() => {
    const initialMap = {};
    if (Array.isArray(initialEvaluation) && initialEvaluation.length > 0) {
      initialEvaluation.forEach((hist) => {
        const critId = hist.criteria?.id || hist.criteria || hist.criteria_id;
        const rId = hist.rating?.id || hist.rating || hist.rating_id;
        if (critId && rId != null) {
          initialMap[critId] = {
            ratingId: rId,
            marks: parseFloat(hist.awarded_marks ?? hist.marks ?? 0),
            comments: hist.comments || '',
          };
        }
      });
    }
    setSelections(initialMap);
    if (initialRemark !== undefined) {
      setOverallRemark(initialRemark || '');
    }
  }, [initialEvaluation, initialRemark]);

  // Ctrl+Enter shortcut for Save & Next
  useEffect(() => {
    if (readOnly) return;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!isSaving && !isStudentView) {
          handleSave(hasNextStudent);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selections, overallRemark, isSaving, isStudentView, hasNextStudent, readOnly]);

  // Handle rating selection (click once to select, click again to deselect)
  const handleSelectRating = (criterion, rating) => {
    if (isStudentView || readOnly) return;
    const critId = criterion.criteria_id || criterion.id;
    const ratingId = rating.rating_id || rating.id;
    const markVal = parseFloat(rating.mark ?? rating.marks ?? 0);

    setSelections((prev) => {
      const current = prev[critId] || prev[String(critId)] || prev[Number(critId)];
      const isAlreadySelected = current && (current.ratingId === ratingId || String(current.ratingId) === String(ratingId));
      if (isAlreadySelected) {
        // Deselect rating on second click
        const next = { ...prev };
        delete next[critId];
        delete next[String(critId)];
        delete next[Number(critId)];
        if (current?.comments) {
          next[critId] = {
            ratingId: null,
            marks: 0,
            comments: current.comments,
          };
        }
        return next;
      }
      return {
        ...prev,
        [critId]: {
          ...(current || {}),
          ratingId: ratingId,
          marks: markVal,
        },
      };
    });
  };

  // Handle criterion comment change
  const handleCriterionCommentChange = (critId, comment) => {
    if (isStudentView || readOnly) return;
    setSelections((prev) => ({
      ...prev,
      [critId]: {
        ...(prev[critId] || {}),
        comments: comment,
      },
    }));
  };

  // Toggle comment box
  const toggleCommentBox = (critId) => {
    setShowCommentBox((prev) => ({
      ...prev,
      [critId]: !prev[critId],
    }));
  };

  // Reset ratings
  const handleReset = () => {
    if (isStudentView) return;
    setSelections({});
    setOverallRemark('');
  };

  // Calculate total marks given
  const calcMarksGiven = () => {
    return Object.values(selections).reduce((acc, curr) => {
      if (curr && curr.ratingId != null) {
        return acc + (parseFloat(curr.marks) || 0);
      }
      return acc;
    }, 0);
  };

  // Get max marks for criterion
  const getCriterionMax = (ratings) => {
    if (!ratings || ratings.length === 0) return 0;
    return Math.max(
      ...ratings.map((r) => parseFloat(r.mark ?? r.marks ?? 0))
    );
  };

  // Total possible rubric score
  const totalRubricScore = rubric.reduce(
    (acc, crit) =>
      acc + getCriterionMax(crit.Ratings || crit.ratings || []),
    0
  );

  const handleSave = async (andNext = false) => {
    if (!onSaveGrade) return;

    const ratingsPayload = {};
    rubric.forEach((crit) => {
      const critId = crit.criteria_id || crit.id;
      const sel = selections[critId] || selections[String(critId)] || selections[Number(critId)];
      if (sel && sel.ratingId != null) {
        ratingsPayload[critId] = {
          rating_id: sel.ratingId,
          marks: sel.marks,
          comments: sel.comments || '',
        };
      }
    });

    await onSaveGrade({
      ratings: ratingsPayload,
      comments: overallRemark,
      andNext: andNext,
    });
  };

  // Render criteria as a horizontal grid (criteria rows x rating columns) - matching RubricViewV2
  const renderCriteriaGrid = () => (
    <div className={styles.gridTable}>
      <div className={styles.gridHeaderRow}>
        <div className={styles.gridCriteriaHead}>Criteria</div>
        <div className={styles.gridRatingHead}>Rating</div>
      </div>
      {rubric.map((criterion, row_index) => {
        const critId = criterion.criteria_id || criterion.id;
        const ratings = criterion.Ratings || criterion.ratings || [];
        const currentSelection = selections[critId] || selections[String(critId)] || selections[Number(critId)];
        const selectedRatingId = currentSelection?.ratingId;
        const score = currentSelection && currentSelection.ratingId != null ? currentSelection.marks : 0;
        const max = getCriterionMax(ratings);

        return (
          <div key={critId || row_index} className={styles.gridRow}>
            <div className={styles.gridCriteriaCell}>
              <div className={styles.gridCriterionTitle}>
                {criterion.name || criterion.title || `Criterion ${row_index + 1}`}
              </div>
              {criterion.description ? (
                <div className={styles.gridCriterionDesc}>{criterion.description}</div>
              ) : null}
              <div className={styles.gridCriterionMarks}>
                {isStudentView ? (
                  <>Marks: <b style={{ color: '#059669' }}>{score}</b>/<b>{max}</b></>
                ) : readOnly ? (
                  <>Max: <b>{max}</b> pts</>
                ) : (
                  <>Marks: <b>{score}</b>/<b>{max}</b></>
                )}
                {!isStudentView && !readOnly && (
                  <OverlayTrigger overlay={<Tooltip>Toggle Comments</Tooltip>}>
                    <button
                      type="button"
                      className={`${styles.commentIconBtn} ${
                        showCommentBox[critId] || currentSelection?.comments ? styles.commentIconActive : ''
                      }`}
                      onClick={() => toggleCommentBox(critId)}
                      title="Add comments for criterion"
                    >
                      <FiMessageSquare size={15} />
                    </button>
                  </OverlayTrigger>
                )}
              </div>
            </div>

            <div className={styles.gridRatingCell}>
              <div className={styles.gridRatingRow}>
                {ratings.map((rating, col_index) => {
                  const ratingId = rating.rating_id || rating.id;
                  const isSelected = selectedRatingId != null && (selectedRatingId === ratingId || String(selectedRatingId) === String(ratingId));
                  const markVal = parseFloat(rating.mark ?? rating.marks ?? 0);
                  const tag =
                    rating.tag || rating.title || String.fromCharCode(65 + col_index);

                  return (
                    <div
                      key={ratingId || col_index}
                      className={`${styles.gridRatingOption} ${
                        isSelected ? styles.gridRatingOptionSelected : ''
                      }`}
                      style={readOnly ? { cursor: 'default' } : undefined}
                      onClick={
                        !isStudentView && !readOnly
                          ? () => handleSelectRating(criterion, rating)
                          : undefined
                      }
                    >
                      <span className={styles.gridRatingTag}>{tag}</span>
                      <span className={styles.gridRatingDesc}>{rating.description || tag}</span>
                      <span className={styles.gridRatingMark}>Mark: {markVal}</span>
                    </div>
                  );
                })}
              </div>

              {((isStudentView && currentSelection?.comments) || (!readOnly && showCommentBox[critId])) && (
                <div className={styles.gridCommentBox}>
                  <div className={styles.commentColumn}>
                    <span className={styles.commentColumnLabel}>
                      {isStudentView ? 'Remarks :' : 'Your Comment:'}
                    </span>
                    <textarea
                      className={styles.commentTextarea}
                      placeholder={isStudentView ? 'No comment' : 'Add your comment...'}
                      value={currentSelection?.comments || ''}
                      onChange={(e) =>
                        handleCriterionCommentChange(critId, e.target.value)
                      }
                      disabled={isStudentView}
                      readOnly={isStudentView}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.rubricContent}>
        {/* Grid / List View Toggle & Question Max Info */}
        <div className={styles.gridControlsHeader}>
          <div className={styles.viewToggle}>
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${
                viewMode === 'list' ? styles.viewToggleBtnActive : ''
              }`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <FiList size={15} /> List
            </button>
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${
                viewMode === 'grid' ? styles.viewToggleBtnActive : ''
              }`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <FiGrid size={15} /> Grid
            </button>
          </div>
        </div>

        {/* Criteria View Mode Switch */}
        {viewMode === 'grid'
          ? renderCriteriaGrid()
          : rubric.map((criterion, row_index) => {
              const critId = criterion.criteria_id || criterion.id;
              const ratings = criterion.Ratings || criterion.ratings || [];
              const currentSelection = selections[critId] || selections[String(critId)] || selections[Number(critId)];
              const selectedRatingId = currentSelection?.ratingId;
              const score = currentSelection && currentSelection.ratingId != null ? currentSelection.marks : 0;
              const max = getCriterionMax(ratings);

              return (
                <div key={critId || row_index} className={styles.criterionCard}>
                  <div className={styles.criterionHeader}>
                    <h3 className={styles.criterionTitle}>
                      {criterion.name || criterion.title || `Criterion ${row_index + 1}`}
                    </h3>
                    {criterion.description ? (
                      <p className={styles.criterionDescription}>
                        {criterion.description}
                      </p>
                    ) : null}
                    <div className={styles.criterionMarks}>
                      {isStudentView ? (
                        <>Marks: <b style={{ color: '#059669' }}>{score}</b>/<b>{max}</b></>
                      ) : readOnly ? (
                        <>Max: <b>{max}</b> pts</>
                      ) : (
                        <>Marks: <b>{score}</b>/<b>{max}</b></>
                      )}
                    </div>
                  </div>

                  <div className={styles.ratingSection}>
                    <div className={styles.ratingLabel}>RATING LEVELS</div>
                    <div className={styles.ratingOptions}>
                      {ratings.map((rating, col_index) => {
                        const ratingId = rating.rating_id || rating.id;
                        const isSelected = selectedRatingId != null && (selectedRatingId === ratingId || String(selectedRatingId) === String(ratingId));
                        const markVal = parseFloat(rating.mark ?? rating.marks ?? 0);
                        const tag =
                          rating.tag || rating.title || String.fromCharCode(65 + col_index);

                        return (
                          <div
                            key={ratingId || col_index}
                            className={`${styles.ratingOption} ${
                              isSelected ? styles.ratingOptionSelected : ''
                            }`}
                            style={readOnly ? { cursor: 'default' } : undefined}
                            onClick={
                              !isStudentView && !readOnly
                                ? () => handleSelectRating(criterion, rating)
                                : undefined
                            }
                          >
                            <Form.Check
                              type="radio"
                              name={`rating-${critId}`}
                              className={styles.ratingRadio}
                              checked={Boolean(isSelected)}
                              onChange={() => {}}
                              disabled={readOnly || isStudentView}
                            />
                            <span className={styles.ratingTag}>{tag}</span>
                            <div className={styles.ratingContent}>
                              <span className={styles.ratingDescription}>
                                {rating.description || tag}
                              </span>
                            </div>
                            <span className={styles.ratingMark}>{markVal} pts</span>

                            {!isStudentView && !readOnly && (
                              <OverlayTrigger overlay={<Tooltip>Toggle Comments</Tooltip>}>
                                <button
                                  type="button"
                                  className={`${styles.commentIconBtn} ${
                                    showCommentBox[critId] || currentSelection?.comments
                                      ? styles.commentIconActive
                                      : ''
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCommentBox(critId);
                                  }}
                                  title="Add comments for criterion"
                                >
                                  <FiMessageSquare size={15} />
                                </button>
                              </OverlayTrigger>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Criterion Comment Box */}
                  {((isStudentView && currentSelection?.comments) || (!readOnly && showCommentBox[critId])) && (
                    <div className={styles.commentBox}>
                      <div className={styles.commentColumn}>
                        <span className={styles.commentColumnLabel}>
                          {isStudentView ? 'Remarks :' : 'Your Comment:'}
                        </span>
                        <textarea
                          className={styles.commentTextarea}
                          placeholder={isStudentView ? 'No comment' : 'Add your comment...'}
                          value={currentSelection?.comments || ''}
                          onChange={(e) =>
                            handleCriterionCommentChange(critId, e.target.value)
                          }
                          disabled={isStudentView}
                          readOnly={isStudentView}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

        {/* Marks Summary - Shown on split screen grading page for instructor and student */}
        {!isEditQuestion && (!readOnly || isStudentView) && (
          <div className={styles.marksSummary}>
            <span className={styles.marksBadgeText}>
              Rubrics Marks Awarded: <b style={{ color: '#059669', fontSize: '15px' }}>{calcMarksGiven()}</b> out of <b>{totalRubricScore} pts</b>
            </span>
          </div>
        )}

        {/* Overall Comment - Shown on split screen grading page for instructor and student */}
        {!isEditQuestion && (!readOnly || isStudentView) && (
          <div className="mb-3">
            <label className="form-label small fw-bold text-muted">
              {isStudentView ? 'Overall Evaluator Remarks:' : 'Overall Instructor Remarks:'}
            </label>
            <textarea
              className="form-control form-control-sm"
              rows={2}
              placeholder={
                isStudentView
                  ? 'No remarks'
                  : 'Remarks visible to student upon score release...'
              }
              value={overallRemark}
              onChange={(e) => setOverallRemark(e.target.value)}
              disabled={isStudentView}
              readOnly={isStudentView}
              style={{ borderRadius: '8px' }}
            />
          </div>
        )}

        {/* Action Buttons - Matching RubricViewV2 with aesthetic modern styling */}
        {!isStudentView && !readOnly && (
          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.submitBtn}
              onClick={() => handleSave(false)}
              disabled={isSaving}
            >
              <FiCheck size={16} />
              <span>{isSaving ? 'Saving...' : 'Save Grade'}</span>
            </button>

            {hasNextStudent && onNextStudent && (
              <button
                type="button"
                className={styles.nextBtn}
                onClick={() => handleSave(true)}
                disabled={isSaving}
                title="Save and advance to next student (Ctrl+Enter)"
              >
                <span>Save & Next</span>
                <FiArrowRight size={16} />
              </button>
            )}

            <button
              type="button"
              className={styles.resetBtn}
              onClick={handleReset}
              disabled={isSaving}
            >
              <FiRotateCcw size={15} />
              <span>Reset</span>
            </button>

            {onClose && (
              <button
                type="button"
                className={styles.hideBtn}
                onClick={onClose}
                disabled={isSaving}
              >
                <FiX size={15} />
                <span>Close</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RubricScoreGrid;
